import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Camera, X, Loader2, ImageIcon } from 'lucide-react';
import { attachmentApi } from '../../services/api';
import { ImageLightbox } from './ImageLightbox';
import { showToast } from '../common';
import type { Attachment } from '../../types';

export interface PendingAttachment {
  id: string;
  fileName: string;
  url: string;
  file?: File;
  isUploading: boolean;
}

interface TaskAttachmentSectionProps {
  existingAttachments?: Attachment[];
  pendingAttachments: PendingAttachment[];
  onPendingChange: React.Dispatch<React.SetStateAction<PendingAttachment[]>>;
  onExistingRemove: (id: string) => void;
  initialFiles?: File[];
  onInitialFilesConsumed?: () => void;
}

export const TaskAttachmentSection: React.FC<TaskAttachmentSectionProps> = ({
  existingAttachments = [],
  pendingAttachments,
  onPendingChange,
  onExistingRemove,
  initialFiles,
  onInitialFilesConsumed,
}) => {
  const { t } = useTranslation();
  const initialFilesRef = useRef<File[] | undefined>(undefined);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Process files shared from the OS PWA share target once
  useEffect(() => {
    if (!initialFiles || initialFiles.length === 0) return;
    if (initialFilesRef.current === initialFiles) return;
    initialFilesRef.current = initialFiles;

    const dt = new DataTransfer();
    initialFiles.forEach((file) => dt.items.add(file));
    handleFiles(dt.files);
    onInitialFilesConsumed?.();
  }, [initialFiles]);

  const allImages = [
    ...existingAttachments.map((a) => a.url),
    ...pendingAttachments.map((a) => a.url),
  ];

  const openLightbox = (index: number) => {
    if (allImages.length === 0) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const removePending = useCallback(async (id: string) => {
    const attachment = pendingAttachments.find((a) => a.id === id);
    if (attachment && !attachment.isUploading) {
      try {
        await attachmentApi.delete(id);
      } catch {
        // Ignore cleanup errors
      }
    }
    onPendingChange(pendingAttachments.filter((a) => a.id !== id));
  }, [pendingAttachments, onPendingChange]);

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      showToast(t('tasks.attachment.selectImageFiles'), 'error');
      return;
    }

    for (const file of imageFiles) {
      const localUrl = URL.createObjectURL(file);
      const placeholder: PendingAttachment = {
        id: `temp-${Math.random().toString(36).slice(2)}`,
        fileName: file.name,
        url: localUrl,
        file,
        isUploading: true,
      };

      onPendingChange((prev) => [...prev, placeholder]);

      try {
        const response = await attachmentApi.uploadTempAttachment(file);
        if (response.success && response.data) {
          const uploaded = response.data as Attachment;
          URL.revokeObjectURL(localUrl);
          onPendingChange((prev) =>
            prev.map((a) =>
              a.id === placeholder.id
                ? { id: uploaded.id, fileName: uploaded.fileName, url: uploaded.url, isUploading: false }
                : a
            )
          );
        } else {
          throw new Error(response.error?.message || t('tasks.attachment.uploadFailed'));
        }
      } catch (error: any) {
        URL.revokeObjectURL(localUrl);
        onPendingChange((prev) => prev.filter((a) => a.id !== placeholder.id));
        showToast(error?.message || t('tasks.attachment.failedToUpload', { fileName: file.name }), 'error');
      }
    }

    // Reset inputs so the same file can be selected again
    if (galleryInputRef.current) galleryInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  }, [onPendingChange]);

  const hasAttachments = existingAttachments.length > 0 || pendingAttachments.length > 0;

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {t('tasks.attachment.images')}
      </label>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => galleryInputRef.current?.click()}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <ImagePlus className="w-4 h-4" />
          {t('tasks.attachment.loadPhoto')}
        </button>
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <Camera className="w-4 h-4" />
          {t('tasks.attachment.camera')}
        </button>

        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          aria-label={t('tasks.attachment.selectFromGallery')}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          aria-label={t('tasks.attachment.takePhoto')}
        />
      </div>

      {hasAttachments && (
        <div className="flex flex-wrap gap-2">
          {existingAttachments.map((attachment, index) => (
            <div
              key={attachment.id}
              className="relative group w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 cursor-pointer"
              onClick={() => openLightbox(index)}
            >
              <img
                src={attachment.url}
                alt={attachment.fileName}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onExistingRemove(attachment.id);
                }}
                className="absolute top-0.5 right-0.5 p-1 rounded-full bg-black/60 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                aria-label={t('tasks.attachment.removeFile', { fileName: attachment.fileName })}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}

          {pendingAttachments.map((attachment, index) => (
            <div
              key={attachment.id}
              className="relative group w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 cursor-pointer"
              onClick={() => openLightbox(existingAttachments.length + index)}
            >
              <img
                src={attachment.url}
                alt={attachment.fileName}
                className="w-full h-full object-cover"
              />
              {attachment.isUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                </div>
              )}
              {!attachment.isUploading && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removePending(attachment.id);
                  }}
                  className="absolute top-0.5 right-0.5 p-1 rounded-full bg-black/60 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  aria-label={t('tasks.attachment.removeFile', { fileName: attachment.fileName })}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!hasAttachments && (
        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
          <ImageIcon className="w-3 h-3" />
          {t('tasks.attachment.noImagesAttached')}
        </p>
      )}

      <ImageLightbox
        images={allImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export default TaskAttachmentSection;
